import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { v1 as uuid } from 'uuid';
import ReducerConstants from '../../_shared/constants/reducers';
import BaseModal from '../../_shared/components/BaseModal';
import theme from '../../_shared/themes';
import { ModalCloseButton } from '../SearchModal/Styles';
import Alert from '../../_shared/components/Alert';
import {
  ModalOuterBoxContainer,
  TitleText,
  BoldDescriptionText,
  StyledWarning
} from './Styles';
import SettingsService from '../../services/SettingsService';
import { httpCodes } from '../../_shared/constants/httpMethods';
import TRToast from '../../_shared/helpers/TRToast';
import RetailToolsDealerLogo from './RetailToolsDealerLogo';
import RetailToolsTiah from './RetailToolsTiah';
import RetailToolsRetailVdp from './RetailToolsRetailVdp';
import InputFieldFormatting from '../../_shared/helpers/InputFieldFormatting';

const propTypes = {
  testId: PropTypes.string,
  isModalOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  isOmp: PropTypes.bool
};

const defaultProps = {
  testId: undefined,
  isModalOpen: false,
  closeHandler: undefined,
  isOmp: false
};

const fieldType = {
  logo: 'logo',
  tiah: 'tiah',
  retailVdp: 'retailVdp'
};

const ConditionalWrapper = ({ children, condition, wrapper }) =>
  condition ? wrapper(children) : children;

const RetailToolsModal = ({ testId, isModalOpen, closeHandler, isOmp }) => {
  const [
    retailCustomizationMetadata,
    setRetailCustomizationMetadata
  ] = useState(null);
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const [dealerData, setDealerData] = useState(null);
  const settingsService = SettingsService.get();
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    const getCustomizationMetadata = async () => {
      const retailCustomizationMetadataResponse = await settingsService.getCustomizationMetadata();
      const dealerRetailInfoResponse = await settingsService.getDealerRetailInfo();

      if (dealerRetailInfoResponse?.status === httpCodes.OK) {
        const dealerRetailInfo = dealerRetailInfoResponse?.data;
        if (dealerRetailInfo) {
          if (dealerRetailInfo?.dealerLogoSrc) {
            setDealerData({
              ...dealerRetailInfo,
              dealerLogoSrc: InputFieldFormatting.formatS3ImageSrc(
                dealerRetailInfo?.dealerLogoSrc
              )
            });
          } else {
            setDealerData(dealerRetailInfo);
          }
          setImgSrc(dealerRetailInfo?.dealerLogoSrc);

          retailCustomizationMetadataResponse.retailVdp.dealershipWebsite.value =
            dealerRetailInfo?.retailVdp?.dealershipLink;
          retailCustomizationMetadataResponse.retailVdp.salespersonName.value =
            dealerRetailInfo?.retailVdp?.salespersonName;
          retailCustomizationMetadataResponse.retailVdp.salespersonEmail.value =
            dealerRetailInfo?.retailVdp?.salespersonEmail;
          retailCustomizationMetadataResponse.retailVdp.salespersonPhone.value =
            dealerRetailInfo?.retailVdp?.salespersonPhone;

          if (dealerRetailInfo?.tiah?.heading != null)
            retailCustomizationMetadataResponse.tiah.heading.value =
              dealerRetailInfo.tiah.heading;
          if (dealerRetailInfo?.tiah?.greeting != null)
            retailCustomizationMetadataResponse.tiah.greeting.value =
              dealerRetailInfo.tiah.greeting;
        }
      } else if (dealerRetailInfoResponse?.isError) {
        if (
          dealerRetailInfoResponse?.errorMessage !==
          'no retail information found'
        )
          TRToast(
            <Alert
              variant="error"
              messages={[dealerRetailInfoResponse?.errorMessage]}
              isOmp={isOmp}
            />
          );
      }
      if (user) {
        if (user.isCanSell && !user.isCanBuy) {
          delete retailCustomizationMetadataResponse[fieldType.retailVdp];
        } else if (user.isCanBuy && !user.isCanSell) {
          delete retailCustomizationMetadataResponse[fieldType.tiah];
        }
      }

      if (retailCustomizationMetadataResponse) {
        const customRetailMetadataList = Object.entries(
          retailCustomizationMetadataResponse
        ).map(([field, item]) => {
          if (item?.displayName && item?.type) {
            return {
              [field]: {
                ...item,
                ref: React.createRef()
              }
            };
          }
          const fieldList = Object.entries(item).map(([subField, subItem]) => {
            return {
              [subField]: {
                ...subItem,
                ref: React.createRef()
              }
            };
          });
          const obj = {
            [field]: Object.assign({}, ...fieldList)
          };
          return obj;
        });

        const customRetailMetadata = Object.assign(
          {},
          ...customRetailMetadataList
        );
        setRetailCustomizationMetadata(customRetailMetadata);
      }
    };

    getCustomizationMetadata();
  }, [settingsService, user, isOmp]);

  return (
    <>
      <ConditionalWrapper
        condition={!isOmp}
        wrapper={children => (
          <BaseModal
            isOpen={isModalOpen}
            isFullScreen
            padding="0"
            bodyOpenClassName="ReactModal__FullScreen"
            backgroundColor={theme.COLORS.NEUTRAL_60}
            isClickEscClose
            onRequestClose={closeHandler}
            data-testid={`${testId}-modal`}
          >
            <ModalCloseButton
              onClick={closeHandler}
              data-testid={`${testId}-closeButton`}
            >
              <FormattedMessage id="list.nav.searchbar.modal.closeButton" />
              <ModalCloseButton.Icon />
            </ModalCloseButton>
            {children}
          </BaseModal>
        )}
      >
        {retailCustomizationMetadata && (
          <ModalOuterBoxContainer>
            <TitleText data-testid={`${testId}-pageHeading`}>
              <FormattedMessage id="retail.tools.setting" />
            </TitleText>
            <BoldDescriptionText data-testid={`${testId}-description`}>
              <FormattedMessage id="retail.tools.setting.description" />
            </BoldDescriptionText>
            <>
              <RetailToolsDealerLogo
                testId={testId}
                companyName={user?.companyName}
                imageSrc={imgSrc}
                isOmp={isOmp}
                customizationData={retailCustomizationMetadata.logo}
                dealerData={dealerData}
              />
              {retailCustomizationMetadata.tiah && (
                <RetailToolsTiah
                  key={uuid()}
                  testId={testId}
                  isOmp={isOmp}
                  companyName={user?.companyName}
                  updateDealerData={setDealerData}
                  dealerData={dealerData}
                  greeting={retailCustomizationMetadata.tiah.greeting}
                  heading={retailCustomizationMetadata.tiah.heading}
                />
              )}
              {retailCustomizationMetadata.retailVdp && (
                <RetailToolsRetailVdp
                  key={uuid()}
                  testId={testId}
                  isOmp={isOmp}
                  companyName={user?.companyName}
                  updateDealerData={setDealerData}
                  dealerData={dealerData}
                  customizationData={retailCustomizationMetadata.retailVdp}
                />
              )}
            </>
            {!isOmp && (
              <>
                <StyledWarning>
                  <FormattedMessage id="retail.tools.setting.warning" />
                </StyledWarning>
                <StyledWarning.Message>
                  <FormattedMessage id="retail.tools.setting.warning.message" />
                </StyledWarning.Message>
              </>
            )}
          </ModalOuterBoxContainer>
        )}
      </ConditionalWrapper>
    </>
  );
};

RetailToolsModal.propTypes = propTypes;
RetailToolsModal.defaultProps = defaultProps;

export default RetailToolsModal;
